<script setup lang="ts">
import ShareSocial from './ShareSocial.vue';

const props = defineProps<{
  isSlideoverOpen: boolean
  selectedBlog: any
  groupedBlocks?: any[]
}>()

// Provide a default empty array for groupedBlocks
const safeGroupedBlocks = computed(() => props.groupedBlocks || [])

const emit = defineEmits<{
  close: []
}>()

// Helper function to convert video URLs to embed URLs
const getVideoEmbedUrl = (url: string) => {
  if (!url) return ''

  // YouTube - support multiple URL formats
  const youtubeMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/
  )
  if (youtubeMatch) {
    const videoId = youtubeMatch[1]
    // Use privacy-enhanced mode and add parameters for better compatibility
    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`
  }

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  }

  return url
}

// Click outside functionality for sidebar
const sidebarRef = ref<HTMLElement | null>(null)

const closeSlideover = () => {
  emit('close')
}

useClickOutside(sidebarRef as Ref<HTMLElement | null>, closeSlideover)

// Keyboard support for closing modal
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isSlideoverOpen) {
    closeSlideover()
  }
}

// Helper to safely get blog date
const formatDate = computed(() => {
  if (!props.selectedBlog?.date) return ''
  try {
    return new Date(props.selectedBlog.date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch (e) {
    return ''
  }
})

// Add keyboard event listener when slideover opens
let scrollY = 0

// Function to lock body scroll
const lockBodyScroll = () => {
  scrollY = window.scrollY
  document.body.style.setProperty('overflow', 'hidden', 'important')
  document.body.style.setProperty('position', 'fixed', 'important')
  document.body.style.setProperty('top', `-${scrollY}px`, 'important')
  document.body.style.setProperty('width', '100%', 'important')
  document.body.style.setProperty('left', '0', 'important')
  document.body.style.setProperty('right', '0', 'important')
  document.documentElement.style.setProperty('overflow', 'hidden', 'important')
}

// Function to unlock body scroll
const unlockBodyScroll = () => {
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('position')
  document.body.style.removeProperty('top')
  document.body.style.removeProperty('width')
  document.body.style.removeProperty('left')
  document.body.style.removeProperty('right')
  document.documentElement.style.removeProperty('overflow')
  window.scrollTo(0, scrollY)
}

// Watch for sidebar open/close
watch(
  () => props.isSlideoverOpen,
  (isOpen) => {
    if (!process.client) return
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
      nextTick(() => {
        lockBodyScroll()
      })
    } else {
      document.removeEventListener('keydown', handleKeydown)
      nextTick(() => {
        unlockBodyScroll()
      })
    }
  },
  { immediate: true }
)

// Cleanup on component unmount
onBeforeUnmount(() => {
  if (!process.client) return
  if (props.isSlideoverOpen) {
    unlockBodyScroll()
    document.removeEventListener('keydown', handleKeydown)
  }
})

const blogUrl = computed(() => {
  if (!props.selectedBlog) return ''
  return encodeURIComponent(`https://nitex.com/page/blog?blog=in-the-end-the-right-culture-wins`)
})

const blogTitle = computed(() => props.selectedBlog?.title || '')
</script>

<template>
  <!-- Blog Sidebar Overlay -->
  <Transition
    enter-active-class="transition-transform duration-700 ease-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transition-transform duration-700 ease-in"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <div
      v-if="isSlideoverOpen"
      class="gap-gap fixed top-0 right-0 z-999 grid w-full grid-cols-6 lg:grid-cols-12"
      tabindex="-1"
    >
      <div
        id="overlay"
        ref="sidebarRef"
        data-lenis-prevent
        :style="['--page-cta-bg: var(--color-black); --page-cta-text: var(--color-neon-yellow);']"
        class="relative z-2 col-span-6 row-start-1 h-screen overflow-x-clip overflow-y-auto overscroll-contain lg:col-span-9 lg:col-start-4"
      >
        <div class="max-lg:mt-[10vh]">
          <!-- Sticky Close Icon -->
          <button
            class="max-lg:p-bleed lg:m-bleed focus:text-neon-yellow bg-neon-yellow fixed top-12 right-4 z-50 flex size-10 h-fit w-fit cursor-pointer items-center justify-center focus:bg-black focus:outline-0 sm:top-16 md:top-12 lg:top-4 lg:size-12"
            aria-label="Close overlay"
            @click="closeSlideover"
          >
            <IconsCross />
          </button>

          <div class="sidbar-body bg-neon-yellow h-fit min-h-[calc(100vh+1px)] w-full pb-40">
            <div class="bg-neon-yellow flex w-full items-center justify-between px-6 py-6">
              <h1
                class="font-display max-w-[calc(100%-var(--grid-bleed)*2-3.5rem)] pt-11 text-3xl uppercase font-stretch-expanded lg:pt-16 lg:text-6xl"
              >
                The Full Story
              </h1>
            </div>
            <div class="translate-container">
              <!-- Blog Content -->
              <div class="space-y-6 p-6 pt-2">
                <!-- Blog Media -->
                <div v-if="selectedBlog?.media" class="w-full">
                  <CommonMedia
                    :sizes="['100vw']"
                    :aspect="5 / 3"
                    :fill="'w'"
                    class="rounded-lg"
                    :media="selectedBlog.media"
                  />
                </div>

                <!-- Blog Meta -->
                <div class="space-y-4">
                  <div v-if="formatDate" class="flex items-center gap-4 text-sm text-black">
                    <span>{{ formatDate }}</span>
                  </div>

                  <!-- Blog Title -->
                  <h1
                    v-if="selectedBlog?.title"
                    class="font-display text-black uppercase font-stretch-expanded sm:text-2xl md:text-3xl"
                  >
                    {{ selectedBlog.title }}
                  </h1>

                  <!-- Blog Description (only shown if no rich text content) -->
                  <p v-if="selectedBlog?.description && (!safeGroupedBlocks || safeGroupedBlocks.length === 0)" class="text-lg leading-relaxed text-black">
                    {{ selectedBlog.description }}
                  </p>
                </div>

                <!-- Rich Text Content -->
                <div v-if="safeGroupedBlocks && safeGroupedBlocks.length > 0" class="space-y-6">
                  <template v-for="item in safeGroupedBlocks" :key="item._key">
                    <!-- H2 Heading (Section 1) -->
                    <h2
                      v-if="item.type === 'block' && item.block._type === 'block' && !item.block.listItem && item.block.style === 'section1' && item.block.children"
                      class="font-display mb-4 mt-8 text-3xl font-bold font-stretch-expanded uppercase text-black"
                    >
                      <template v-for="(child, childIndex) in item.block.children" :key="childIndex">
                        <span>{{ child.text }}</span>
                      </template>
                    </h2>

                    <!-- H3 Heading (Section 2) -->
                    <h3
                      v-else-if="item.type === 'block' && item.block._type === 'block' && !item.block.listItem && item.block.style === 'section2' && item.block.children"
                      class="mb-3 mt-6 text-xl leading-relaxed text-black"
                    >
                      <template v-for="(child, childIndex) in item.block.children" :key="childIndex">
                        <span>{{ child.text }}</span>
                      </template>
                    </h3>

                    <!-- Blockquote (Quote Style) -->
                    <blockquote
                      v-else-if="item.type === 'block' && item.block._type === 'block' && !item.block.listItem && item.block.style === 'blockquote' && item.block.children"
                    >
                      <div class="font-display text-xl uppercase font-stretch-expanded lg:text-3xl">
                        <template v-for="(child, childIndex) in item.block.children" :key="childIndex">
                          <!-- Check for link marks -->
                          <template
                            v-if="
                              child.marks &&
                              child.marks.length > 0 &&
                              item.block.markDefs &&
                              child.marks.some((mark: string) =>
                                item.block.markDefs?.find((def: any) => def._key === mark)
                              )
                            "
                          >
                            <a
                              :href="
                                item.block.markDefs.find((def: any) =>
                                  child.marks?.includes(def._key)
                                )?.href
                              "
                              :target="
                                item.block.markDefs.find((def: any) =>
                                  child.marks?.includes(def._key)
                                )?.openInNewTab
                                  ? '_blank'
                                  : '_self'
                              "
                              class="text-black underline hover:text-gray-700"
                              rel="noopener noreferrer"
                            >
                              <strong v-if="child.marks.includes('strong')">{{
                                child.text
                              }}</strong>
                              <em v-else-if="child.marks.includes('em')">{{ child.text }}</em>
                              <span v-else>{{ child.text }}</span>
                            </a>
                          </template>
                          <strong
                            v-else-if="child.marks && child.marks.includes('strong')"
                            >{{ child.text }}</strong
                          >
                          <em v-else-if="child.marks && child.marks.includes('em')">{{
                            child.text
                          }}</em>
                          <span v-else>{{ child.text }}</span>
                        </template>
                      </div>
                    </blockquote>

                    <!-- Regular Text Block -->
                    <div
                      v-else-if="item.type === 'block' && item.block._type === 'block' && !item.block.listItem && (!item.block.style || item.block.style === 'normal')"
                      class="prose prose-lg max-w-none text-black"
                    >
                      <p
                        v-if="item.block.children && item.block.children.length > 0"
                        class="leading-relaxed whitespace-pre-line"
                      >
                        <template v-for="(child, childIndex) in item.block.children" :key="childIndex">
                          <!-- Check for link marks -->
                          <template
                            v-if="
                              child.marks &&
                              child.marks.length > 0 &&
                              item.block.markDefs &&
                              child.marks.some((mark: string) =>
                                item.block.markDefs?.find((def: any) => def._key === mark)
                              )
                            "
                          >
                            <a
                              :href="
                                item.block.markDefs.find((def: any) =>
                                  child.marks?.includes(def._key)
                                )?.href
                              "
                              :target="
                                item.block.markDefs.find((def: any) =>
                                  child.marks?.includes(def._key)
                                )?.openInNewTab
                                  ? '_blank'
                                  : '_self'
                              "
                              class="text-black underline hover:text-gray-700"
                              rel="noopener noreferrer"
                            >
                              <strong v-if="child.marks.includes('strong')">{{
                                child.text
                              }}</strong>
                              <em v-else-if="child.marks.includes('em')">{{ child.text }}</em>
                              <span v-else>{{ child.text }}</span>
                            </a>
                          </template>
                          <strong
                            v-else-if="child.marks && child.marks.includes('strong')"
                            >{{ child.text }}</strong
                          >
                          <em v-else-if="child.marks && child.marks.includes('em')">{{
                            child.text
                          }}</em>
                          <span v-else>{{ child.text }}</span>
                        </template>
                      </p>
                    </div>

                    <!-- Bullet List -->
                    <ul
                      v-else-if="item.type === 'list' && item.listType === 'bullet'"
                      class="prose prose-lg max-w-none list-disc space-y-2 pl-6 text-black"
                    >
                      <li
                        v-for="(listBlock, listIndex) in item.items"
                        :key="listBlock._key || `li-${listIndex}`"
                        class="leading-relaxed"
                      >
                        <template
                          v-for="(child, childIndex) in listBlock.children"
                          :key="childIndex"
                        >
                          <strong v-if="child.marks && child.marks.includes('strong')">{{
                            child.text
                          }}</strong>
                          <em v-else-if="child.marks && child.marks.includes('em')">{{
                            child.text
                          }}</em>
                          <span v-else>{{ child.text }}</span>
                        </template>
                      </li>
                    </ul>

                    <!-- Numbered List -->
                    <ol
                      v-else-if="item.type === 'list' && item.listType === 'number'"
                      class="prose prose-lg max-w-none list-decimal space-y-2 pl-6 text-black"
                    >
                      <li
                        v-for="(listBlock, listIndex) in item.items"
                        :key="listBlock._key || `li-${listIndex}`"
                        class="leading-relaxed"
                      >
                        <template
                          v-for="(child, childIndex) in listBlock.children"
                          :key="childIndex"
                        >
                          <strong v-if="child.marks && child.marks.includes('strong')">{{
                            child.text
                          }}</strong>
                          <em v-else-if="child.marks && child.marks.includes('em')">{{
                            child.text
                          }}</em>
                          <span v-else>{{ child.text }}</span>
                        </template>
                      </li>
                    </ol>

                    <!-- Single Image -->
                    <div v-else-if="item.type === 'block' && item.block._type === 'image'" class="w-full h-auto">
                      <img
                        v-if="item.block?.asset?.url"
                        :src="item.block.asset.url"
                        :alt="item.block.alt || 'Blog image'"
                        class="w-full object-cover"
                      />
                    </div>

                    <!-- Image Gallery -->
                    <div v-else-if="item.type === 'block' && item.block._type === 'gallery' && item.block.images" class="w-full">
                      <!-- Horizontal (Grid) Layout -->
                      <div
                        v-if="!item.block.layout || item.block.layout === 'grid'"
                        class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4"
                      >
                        <template v-for="(image, imgIndex) in item.block.images" :key="imgIndex">
                          <div
                            v-if="image.asset?.url"
                            class="flex-shrink-0 w-full h-48 overflow-hidden"
                          >
                            <img
                              :src="image.asset.url"
                              :alt="image.alt || `Gallery image ${imgIndex + 1}`"
                              class="w-full h-full object-cover"
                            />
                          </div>
                        </template>
                      </div>

                      <!-- Vertical (Masonry) Layout -->
                      <div
                        v-else-if="item.block.layout === 'masonry'"
                        class="columns-2 md:columns-3 lg:columns-5 gap-4"
                      >
                        <template v-for="(image, imgIndex) in item.block.images" :key="imgIndex">
                          <div
                            v-if="image.asset?.url"
                            class="mb-4 break-inside-avoid overflow-hidden"
                            style="aspect-ratio: 0.6 / 1;"
                          >
                            <img
                              :src="image.asset.url"
                              :alt="image.alt || `Gallery image ${imgIndex + 1}`"
                              class="h-full w-full object-cover"
                            />
                          </div>
                        </template>
                      </div>
                    </div>

                    <!-- Video Embed -->
                    <div v-else-if="item.type === 'block' && item.block._type === 'video' && item.block.url" class="aspect-video w-full">
                      <iframe
                        :src="getVideoEmbedUrl(item.block.url)"
                        class="h-full w-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                        loading="lazy"
                        title="Embedded video"
                      />
                    </div>

                    <!-- Callout Box -->
                    <div
                      v-else-if="item.type === 'block' && item.block._type === 'callout' && item.block.text"
                      class="border-l-4 p-1"
                      :class="{
                        'border-blue-500': item.block.type === 'info',
                        'border-yellow-500': item.block.type === 'warning',
                        'border-green-500': item.block.type === 'success',
                        'border-red-500': item.block.type === 'error',
                      }"
                    >
                      <p class="whitespace-pre-line text-black">{{ item.block.text }}</p>
                    </div>
                  </template>
                </div>
                
                <!-- Share Social Media Buttons -->
                <ShareSocial
                  :blog-url="blogUrl"
                  :blog-title="blogTitle"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
<style lang="postcss">
.vertical-text {
  writing-mode: vertical-rl;
  text-orientation: mixed;
}
</style>

